import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import type { RegisterEntraGroupDto } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useIsTenantAdmin } from '../../auth/useWorkspaceAdmin';
import { useTenantKey } from '../../lib/useTenantKey';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { FormField, Input, Select } from '../../components/ui/Field';
import { useFieldId } from '../../components/ui/useFieldId';
import { useDialogs } from '../../components/ui/DialogProvider';
import { CHANGES_SAVED, useToast } from '../../components/ui/toast';
import { useSaveHold } from '../../components/settings/footerSave';

/**
 * Register Microsoft 365 / Entra groups (Object ID + admin label) as a SOURCE of
 * teammates an admin can invite into a workspace (the picker lives in the Members card).
 * Lists registered groups (name + Object ID) with an admin-gated Remove, plus a form
 * to register a new one. Registrations are INSTALL-WIDE (shared across all Brand Workspaces);
 * writes invalidate `['entra-groups']`.
 *
 * Registrations are INSTALL-LEVEL (site-wide config), so managing them (pick/register/remove)
 * is **Tenant-Admin-only** — the API enforces it; this gate just hides the write UI for others.
 */
export function EntraGroupsCard() {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const isAdmin = useIsTenantAdmin();
  const { confirm, alert } = useDialogs();
  // Register group and Remove save at once: each confirms with the app's "Changes saved" toast, and
  // leaving the tab or the page waits for its answer (useSaveHold).
  const toast = useToast();
  const hold = useSaveHold();

  const groupsQuery = useQuery({
    // Install-wide (not tenant-scoped) — registered groups are shared by every Brand Workspace,
    // so the key omits tenantKey and the list persists across workspace switches.
    queryKey: ['entra-groups'],
    queryFn: ({ signal }) => api.listEntraGroups(signal),
    enabled: tenantKey !== '',
  });
  const groups = groupsQuery.data ?? [];

  // The app's Entra directory (tenant) id — shown so the admin knows which directory
  // these groups come from.
  const directoryQuery = useQuery({
    queryKey: ['entra-directory', tenantKey],
    queryFn: ({ signal }) => api.entraDirectory(signal),
    enabled: tenantKey !== '',
  });

  // The directory's Microsoft 365 groups, for the picker (admin only — Graph-gated).
  const availableQuery = useQuery({
    queryKey: ['entra-available-groups', tenantKey],
    queryFn: ({ signal }) => api.availableEntraGroups(signal),
    enabled: tenantKey !== '' && isAdmin,
  });
  const available = availableQuery.data ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['entra-groups'] });

  const nameId = useFieldId('entra-group-name');
  const objectIdId = useFieldId('entra-group-object-id');
  const pickId = useFieldId('entra-group-pick');
  const [name, setName] = useState('');
  const [objectId, setObjectId] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  /** Prefill the form from a picked directory group (name + Object ID, both still editable). */
  const onPick = (pickedObjectId: string) => {
    setAddError(null);
    const g = available.find((x) => x.objectId === pickedObjectId);
    setObjectId(pickedObjectId);
    if (g) setName(g.name);
  };

  const add = useMutation({
    mutationFn: () => {
      const dto: RegisterEntraGroupDto = { objectId: objectId.trim(), name: name.trim() };
      return api.registerEntraGroup(dto);
    },
    onSuccess: () => {
      void invalidate();
      setName('');
      setObjectId('');
      setAddError(null);
      toast.show(CHANGES_SAVED);
    },
    onError: (err) =>
      setAddError(err instanceof ApiRequestError ? err.message : 'Could not register this group.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteEntraGroup(id),
    onSuccess: () => {
      void invalidate();
      toast.show(CHANGES_SAVED);
    },
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: err instanceof ApiRequestError ? err.message : 'Could not remove this group.',
      }),
  });

  const canAdd = name.trim() !== '' && objectId.trim() !== '' && !add.isPending;

  return (
    <Card>
      <CardHeader
        title="Microsoft 365 Groups"
        description="Register Entra (Microsoft 365) groups as a source of teammates you can invite into this workspace."
      />
      <CardBody className="space-y-5">
        {/* Which Entra directory (and app) these groups come from. */}
        {directoryQuery.data?.tenantId && (
          <div className="space-y-1">
            <p className="text-xs text-ink-muted">
              Microsoft Entra Directory (Tenant) ID:{' '}
              <span className="font-mono text-ink-strong">{directoryQuery.data.tenantId}</span>
            </p>
            {directoryQuery.data.appName && (
              <p className="text-xs text-ink-muted">
                Microsoft Entra app:{' '}
                <span className="text-ink-strong">{directoryQuery.data.appName}</span>
              </p>
            )}
          </div>
        )}

        {/* Registered groups */}
        {groupsQuery.isLoading ? (
          <p className="text-sm text-ink-faint">Loading groups…</p>
        ) : groupsQuery.isError ? (
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load registered groups.</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-ink-muted">
            No Microsoft 365 groups registered yet. Add one below.
          </p>
        ) : (
          <ul className="space-y-2">
            {groups.map((g) => (
              <li
                key={g.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink">{g.name}</p>
                  <p className="truncate font-mono text-xs text-ink-muted">{g.objectId}</p>
                </div>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={remove.isPending && remove.variables === g.id}
                    onClick={async () => {
                      if (
                        await confirm({
                          title: 'Remove group',
                          message: `Remove the group “${g.name}”?`,
                          confirmLabel: 'Remove',
                          danger: true,
                        })
                      )
                        void hold(remove.mutateAsync(g.id));
                    }}
                    className="text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden /> Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Register a new group (admin only) — collapsible to reduce clutter (default collapsed). */}
        {isAdmin && (
          <details className="group border-t border-line-soft pt-5">
            <summary className="focus-ring flex cursor-pointer list-none items-center justify-between gap-2 rounded text-sm font-medium text-ink-strong [&::-webkit-details-marker]:hidden">
              <span>Register a group</span>
              <ChevronDown
                className="h-4 w-4 shrink-0 text-ink-faint transition group-open:rotate-180"
                aria-hidden
              />
            </summary>
            <div className="space-y-4 pt-4">
            {/* Primary: pick from the directory's Microsoft 365 groups. */}
            <FormField
              label="Choose a Microsoft 365 group"
              htmlFor={pickId}
              hint={
                availableQuery.isError
                  ? "Couldn't list your groups yet (the directory permission may still be propagating — try again shortly). You can enter an Object ID manually below."
                  : 'Pick a group from your Microsoft 365 directory.'
              }
            >
              <Select
                id={pickId}
                value={objectId}
                disabled={availableQuery.isLoading || available.length === 0}
                onChange={(e) => onPick(e.target.value)}
              >
                <option value="">
                  {availableQuery.isLoading
                    ? 'Loading groups…'
                    : available.length === 0
                      ? 'No groups available'
                      : 'Select a group…'}
                </option>
                {available.map((g) => (
                  <option key={g.objectId} value={g.objectId}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </FormField>

            {/* Name + Object ID — auto-filled by the picker, editable, or entered manually. */}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Name" htmlFor={nameId} hint="A label to recognize this group.">
                <Input
                  id={nameId}
                  value={name}
                  onChange={(e) => {
                    setAddError(null);
                    setName(e.target.value);
                  }}
                  placeholder="e.g. Marketing Team"
                />
              </FormField>
              <FormField
                label="Object ID"
                htmlFor={objectIdId}
                hint="Auto-filled when you pick a group above, or paste a group's Object ID."
              >
                <Input
                  id={objectIdId}
                  value={objectId}
                  onChange={(e) => {
                    setAddError(null);
                    setObjectId(e.target.value);
                  }}
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </FormField>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                loading={add.isPending}
                disabled={!canAdd}
                onClick={() => {
                  if (!canAdd) return;
                  setAddError(null);
                  void hold(add.mutateAsync());
                }}
              >
                <Plus className="h-4 w-4" aria-hidden /> Register group
              </Button>
              {addError && <span className="text-sm text-red-600 dark:text-error-dark">{addError}</span>}
            </div>
            </div>
          </details>
        )}
      </CardBody>
    </Card>
  );
}
